// The contents' filter (the Mac's LibraryModel+Filter; `design-docs/Binder filters — design.md`): facets as chips plus
// the words typed in the bar, tested on what the open project holds (`liveFacts`; the text through each document's
// words, kept while the document is unchanged, where the Mac asks its index), and shown as the arrangement's tree with
// the folders above the matches as faint context. The pages are given that tree, so Plan and Write follow it.
// Collections are saved filters: one loaded keeps its id, so the bar can say "(edited)" and Update it.
//
// The filter in use is kept per browser and project (`binderFilter-<project>`, as the Mac's UserDefaults key), never
// synced. Everything here is pure (no DOM, no React) so the unit tests run it in Node; the shell holds the state.
import {
  makeID, StandardField, type FragmentKind, type FragmentRecord, type OpenedProject, type Placement,
  type ProjectType, type StoredArrangement, type TreeNode,
} from '@writing-suite/core';
import {
  beatSheetOf, decodeSavedSearch, encodeSavedSearch, filterContext, FilterResult, indexedBody, isTextRule, liveFacts, newRule, ruleValue, sameFilter, searchMatches,
  WordQuery, type SavedSearch, type SavedSearchRule,
} from '@writing-suite/core/plan';
import { readCommentCategories } from '@writing-suite/core/revise';
import { presenceOf } from './story.ts';
import { cssColor, formatNumber } from '../views/helpers.ts';
import type { IconName, Ink } from '../views/Icons.tsx';

// MARK: The state

/** The typed words' rule id (never stored: a collection saved from it gets its own). */
export const TYPED_RULE_ID = 'typed-text';

/** What this browser keeps of a project's filter: the chips (a collection's own id when loaded from one), the words
 *  in the bar, and whether the contents show the matches as a list. */
export interface BinderFilterState {
  filter: SavedSearch | null;
  text: string;
  list: boolean;
}

export const NO_FILTER: BinderFilterState = { filter: null, text: '', list: false };

/** The preference key (`PREF.binderFilter`). */
export const filterKey = (project: string) => `binderFilter-${project}`;

/** As stored: the filter in the JSON collections are kept in. */
export const storedFilter = (s: BinderFilterState) => ({ filter: s.filter ? encodeSavedSearch(s.filter) : null, text: s.text, list: s.list });

const read = new WeakMap<object, BinderFilterState>();

/** What's stored, read (the same state for the same stored object, so React sees no change); anything unreadable is no
 *  filter. */
export function readStoredFilter(raw: unknown): BinderFilterState {
  if (!raw || typeof raw !== 'object') return NO_FILTER;
  const hit = read.get(raw);
  if (hit) return hit;
  const o = raw as Record<string, unknown>;
  const state: BinderFilterState = {
    filter: o.filter ? decodeSavedSearch(o.filter) : null,
    text: typeof o.text === 'string' ? o.text : '',
    list: o.list === true,
  };
  read.set(raw, state);
  return state;
}

/** A facet added from a menu but not chosen yet (Status with no option ticked, Title with no words): it shows as a chip
 *  while its editor is open, and narrows nothing. */
export function isUnfilled(rule: SavedSearchRule): boolean {
  switch (rule.kind) {
    case 'field':
      if (rule.op === 'empty') return false;
      if (rule.op === 'contains') return new WordQuery(ruleValue(rule) ?? '').isEmpty;
      return rule.values.length === 0;
    case 'entity': case 'kind': case 'within':
      return rule.values.length === 0;
    case 'text': case 'title': case 'synopsis': case 'notes':
      return rule.op !== 'empty' && new WordQuery(ruleValue(rule) ?? '').isEmpty;
    default:
      return false;
  }
}

/** `activeFilter`: the chips and the typed words; null when nothing narrows the contents. */
export function activeFilter(s: BinderFilterState): SavedSearch | null {
  const rules = (s.filter?.rules ?? []).filter((r) => !isUnfilled(r));
  const words = s.text.trim();
  if (words) rules.push(newRule({ id: TYPED_RULE_ID, kind: 'text', value: words }));
  return rules.length ? { ...(s.filter ?? { id: '', name: '', scope: 'manuscript', matchAll: true, rules: [] }), rules } : null;
}

/** The collection the filter was loaded from (or saved as), if it's still one. */
export const selectedSearch = (s: BinderFilterState, searches: readonly SavedSearch[]) =>
  (s.filter ? searches.find((x) => x.id === s.filter!.id) : undefined) ?? null;

/** `setFilterRules`: an unsaved filter with no chips is none; a collection's stays loaded (it can be updated). */
export function withRules(s: BinderFilterState, rules: SavedSearchRule[], saved: boolean): BinderFilterState {
  if (!rules.length && !saved) return { ...s, filter: null };
  return { ...s, filter: { ...(s.filter ?? { id: makeID(), name: '', scope: 'manuscript', matchAll: true, rules: [] }), rules } };
}

/** Values for the same facet join it ("Status: Draft" + "Revised" = "Draft or Revised"). */
function joins(a: SavedSearchRule, b: SavedSearchRule): boolean {
  if (a.kind !== b.kind || a.field !== b.field || a.negate !== b.negate || a.op !== undefined || b.op !== undefined || a.level !== b.level || a.all !== b.all) return false;
  return ['field', 'entity', 'kind'].includes(a.kind) && a.values.length > 0 && b.values.length > 0;
}

/** `addFacet`. */
export function addFacet(s: BinderFilterState, rule: SavedSearchRule, saved = false): BinderFilterState {
  const rules = [...(s.filter?.rules ?? [])];
  const i = rules.findIndex((r) => joins(r, rule));
  if (i >= 0) rules[i] = { ...rules[i], values: [...rules[i].values, ...rule.values.filter((v) => !rules[i].values.includes(v))] };
  else rules.push(rule);
  return withRules(s, rules, saved);
}

export function replaceFacet(s: BinderFilterState, rule: SavedSearchRule, saved = false): BinderFilterState {
  const rules = s.filter?.rules ?? [];
  if (!rules.some((r) => r.id === rule.id)) return addFacet(s, rule, saved);
  return withRules(s, rules.map((r) => (r.id === rule.id ? rule : r)), saved);
}

export function removeFacet(s: BinderFilterState, id: string, saved = false): BinderFilterState {
  if (id === TYPED_RULE_ID) return { ...s, text: '' };
  return withRules(s, (s.filter?.rules ?? []).filter((r) => r.id !== id), saved);
}

/** Filters by these facets alone (a tag chip, Filter By ▸ POV: Hyde), or adds them to the filter in use. */
export function filterBy(s: BinderFilterState, rules: SavedSearchRule[], adding: boolean, saved = false): BinderFilterState {
  if (adding) return rules.reduce((x, r) => addFacet(x, r, saved), s);
  return { ...s, filter: { id: makeID(), name: '', scope: 'manuscript', matchAll: true, rules }, text: '' };
}

export const clearFilter = (s: BinderFilterState): BinderFilterState => ({ ...s, filter: null, text: '' });

/** `loadFilter`: a collection's words go in the bar, its other rules become chips (its id kept). */
export function loadFilter(s: BinderFilterState, search: SavedSearch): BinderFilterState {
  const rules = [...search.rules];
  const i = rules.findIndex((r) => r.kind === 'text' && !r.negate);
  const words = i >= 0 ? (ruleValue(rules[i]) ?? '') : '';
  if (i >= 0) rules.splice(i, 1);
  return { ...s, filter: { ...search, rules }, text: words };
}

/** The filter as a collection to save: its own id, the typed words as a rule, the scope by the arrangement shown. */
export function filterAsCollection(s: BinderFilterState, name: string, scope: SavedSearch['scope']): SavedSearch | null {
  const filter = activeFilter(s);
  if (!filter) return null;
  return { id: makeID(), name, scope, matchAll: filter.matchAll, rules: filter.rules.map((r) => (r.id === TYPED_RULE_ID ? { ...r, id: makeID() } : r)) };
}

/** "Hyde's thread (edited)": the filter no longer says what the collection it came from says. */
export function isFilterEdited(s: BinderFilterState, saved: SavedSearch | null): boolean {
  const filter = activeFilter(s);
  if (!saved) return false;
  if (!filter) return saved.rules.length > 0;
  return !sameFilter({ ...filter, scope: saved.scope }, saved);
}

/** The folder the filter keeps to (Focus), if any. */
export const focusNodeId = (s: BinderFilterState): string | null =>
  s.filter?.rules.find((r) => r.kind === 'within' && !r.negate)?.values[0] ?? null;

/** Focus on a folder (a Within facet first, combined with the rest), or none. */
export function focus(s: BinderFilterState, nodeId: string | null, saved = false): BinderFilterState {
  const rules = (s.filter?.rules ?? []).filter((r) => r.kind !== 'within');
  if (nodeId) rules.unshift(newRule({ kind: 'within', value: nodeId }));
  return withRules(s, rules, saved);
}

// MARK: Testing the documents

const SPLIT = new WeakMap<object, { body?: string; bodyWords?: string[]; notesWords?: string[]; folded?: string }>();

function cacheOf(f: FragmentRecord) {
  let c = SPLIT.get(f);
  if (!c) SPLIT.set(f, (c = {}));
  return c;
}

/** A document's plain text, as word counts and the index read it (a script's printed text, a map's notes and labels). */
export function bodyText(f: FragmentRecord): string {
  const c = cacheOf(f);
  if (c.body === undefined) c.body = indexedBody(f);
  return c.body;
}

/** A document's words as `WordQuery` splits them, kept while the document is unchanged. */
function wordsOf(f: FragmentRecord, column: 'body' | 'notes'): string[] {
  const c = cacheOf(f);
  if (column === 'notes') return (c.notesWords ??= WordQuery.words(f.notes));
  return (c.bodyWords ??= WordQuery.words(bodyText(f)));
}

/** What the filter shows of an arrangement. */
export interface FilterView {
  /** The arrangement and filter it was made for (a document kept after an edit stays only while this is the same). */
  key: string;
  result: FilterResult;
  /** A document edited out of the filter while it was selected: shown faintly until the selection moves. */
  kept: string | null;
  /** The typed words (or a collection's text rule), for the snippets under the rows. */
  query: WordQuery | null;
}

export const filterViewKey = (arrangementId: string, filter: SavedSearch) =>
  `${arrangementId}|${JSON.stringify(filter.rules.map((r) => ({ ...r, id: r.id === TYPED_RULE_ID ? r.id : '' })))}|${filter.matchAll}`;

/** The arrangement filtered: every node with a fragment tested on the live facts, the text rules on each document's
 *  words. */
export function filterArrangement(project: OpenedProject, arrangement: StoredArrangement, filter: SavedSearch, o: {
  locks: ReadonlySet<string>;
  now?: Date;
}): FilterResult {
  const needsPresence = filter.rules.some((r) => r.kind === 'entity');
  const ids = new Set<string>();
  for (const n of arrangement.nodes.values()) if (n.fragmentID !== undefined) ids.add(n.fragmentID);
  const presence = needsPresence
    ? [...ids].flatMap((id) => presenceOf(project, id).map((p) => ({ fragmentID: id, entityID: p.entityID, level: p.level })))
    : [];
  const facts = liveFacts(arrangement, {
    fragment: (id) => project.fragmentRecord(id), fields: project.record.fields, presence, locks: o.locks, words: (id) => project.words(id),
  });
  const textMatches = new Map<string, Set<string>>();
  for (const rule of filter.rules) {
    if (!isTextRule(rule)) continue;
    const query = new WordQuery(ruleValue(rule) ?? '');
    const column = rule.kind === 'notes' ? 'notes' : 'body';
    const found = new Set<string>();
    for (const id of ids) {
      const f = project.fragmentRecord(id);
      if (f && query.matchesWords(wordsOf(f, column))) found.add(id);
    }
    textMatches.set(rule.id, found);
  }
  const context = filterContext({ fields: project.record.fields, textMatches, now: o.now });
  return FilterResult.make(arrangement, (id) => {
    const f = facts.get(id);
    return !!f && searchMatches(filter, f, context);
  }, (id) => facts.get(id)?.words ?? 0);
}

/** `filteredManuscript`: the manuscript's fragments the filter lets through, whichever arrangement shows, and how many
 *  documents of how many (Publish ▸ only the filtered scenes). */
export function filteredManuscript(project: OpenedProject, filter: SavedSearch, locks: ReadonlySet<string>):
  { fragments: string[]; documents: number; total: number } | null {
  const m = project.record.arrangements.find((a) => a.role === 'manuscript');
  if (!m) return null;
  const result = filterArrangement(project, m, filter, { locks });
  return { fragments: [...result.matches].flatMap((id) => m.nodes.get(id)?.fragmentID ?? []), documents: result.documents, total: result.total };
}

/** The words a filter's text facet looks for (the typed words first), for snippets. */
export function snippetQuery(filter: SavedSearch | null): WordQuery | null {
  const rule = filter?.rules.find((r) => r.id === TYPED_RULE_ID) ?? filter?.rules.find((r) => r.kind === 'text' && !r.negate);
  const query = rule ? new WordQuery(ruleValue(rule) ?? '') : null;
  return query && !query.isEmpty ? query : null;
}

/** `filterState`'s keeping: the selected row stays while an edit takes it out of the filter (it doesn't vanish under the
 *  pointer), with the folders above it as context, until the selection moves or the filter changes. */
export function keepSelection(previous: FilterView | null, key: string, result: FilterResult, selected: string | null, tree: TreeNode[]):
  { result: FilterResult; kept: string | null } {
  if (!previous || previous.key !== key || !selected || result.matches.has(selected)
    || !(previous.result.matches.has(selected) || previous.kept === selected)) return { result, kept: null };
  const path = pathOf(tree, selected);
  if (!path) return { result, kept: null };
  const kept: FilterResult = { ...result, matches: new Set(result.matches).add(selected), context: new Set(result.context), opened: new Set(result.opened) };
  for (const n of path.slice(0, -1)) {
    if (!kept.matches.has(n.id)) kept.context.add(n.id);
    kept.opened.add(n.id);
  }
  return { result: kept, kept: selected };
}

function pathOf(nodes: TreeNode[], id: string, trail: TreeNode[] = []): TreeNode[] | null {
  for (const n of nodes) {
    if (n.id === id) return [...trail, n];
    const inner = pathOf(n.children, id, [...trail, n]);
    if (inner) return inner;
  }
  return null;
}

/** The tree as the filter shows it: only the matches and the folders above them. */
export function visibleTree(tree: TreeNode[], result: FilterResult): TreeNode[] {
  const prune = (nodes: TreeNode[]): TreeNode[] =>
    nodes.filter((n) => FilterResult.shows(result, n.id)).map((n) => ({ ...n, children: prune(n.children) }));
  return prune(tree);
}

/** A node as the filter shows it (its children pruned), for a selection the filter doesn't show. */
export function visibleNode(node: TreeNode, result: FilterResult): TreeNode {
  return { ...node, children: visibleTree(node.children, result) };
}

/** The matching rows in reading order (List mode). */
export function orderedMatches(tree: TreeNode[], result: FilterResult): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (result.matches.has(n.id)) out.push(n);
      walk(n.children);
    }
  };
  walk(tree);
  return out;
}

// MARK: Snippets

export interface Snippet {
  before: string;
  hit: string;
  after: string;
}

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** Text around the first place the words are found: ~40 characters before, the word, ~80 after, cut at spaces. */
export function snippet(text: string, query: WordQuery): Snippet | null {
  const first = query.terms[0];
  if (!first?.length) return null;
  // Folded a character at a time (kept where folding would change the length), so offsets stay the text's.
  const c = /[^\p{ASCII}]/u.test(text) ? [...text].map((ch) => { const f = fold(ch); return f.length === ch.length ? f : ch; }).join('') : text.toLowerCase();
  const word = first[0];
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = new RegExp(`(?<![\\p{L}\\p{N}])${escaped}[\\p{L}\\p{N}]*`, 'u').exec(c);
  if (!m) return null;
  let start = Math.max(0, m.index - 40);
  if (start > 0) {
    const space = text.indexOf(' ', start);
    if (space >= 0 && space < m.index) start = space + 1;
  }
  let end = Math.min(text.length, m.index + m[0].length + 80);
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end);
    if (space > m.index + m[0].length) end = space;
  }
  const squash = (s: string) => s.replace(/\s+/g, ' ');
  return {
    before: (start > 0 ? '…' : '') + squash(text.slice(start, m.index)),
    hit: text.slice(m.index, m.index + m[0].length),
    after: squash(text.slice(m.index + m[0].length, end)) + (end < text.length ? '…' : ''),
  };
}

/** A document's snippet for the filter's words (kept while the document is unchanged). */
export function fragmentSnippet(project: OpenedProject, fragmentId: string, query: WordQuery): Snippet | null {
  const f = project.fragmentRecord(fragmentId);
  if (!f) return null;
  const key = JSON.stringify(query.terms);
  let byQuery = SNIPPETS.get(f);
  if (!byQuery) SNIPPETS.set(f, (byQuery = new Map()));
  if (!byQuery.has(key)) byQuery.set(key, snippet(bodyText(f), query));
  return byQuery.get(key) ?? null;
}

const SNIPPETS = new WeakMap<object, Map<string, Snippet | null>>();

// MARK: Next and Previous Document

/** The documents to step through: every one in reading order, or the filter's matches (and a kept one). */
export function steppable(tree: TreeNode[], view: Pick<FilterView, 'result' | 'kept'> | null): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.fragmentId && !n.isFolder && (!view || view.result.matches.has(n.id) || view.kept === n.id)) out.push(n);
      walk(n.children);
    }
  };
  walk(tree);
  return out;
}

/** `stepDocument`: the next (or previous) document from the selection; from a folder (or nothing), the first document
 *  after it in reading order (or the last before it). Null at the end. */
export function stepTarget(tree: TreeNode[], view: Pick<FilterView, 'result' | 'kept'> | null, selected: string | null, forward: boolean): string | null {
  const nodes = steppable(tree, view);
  if (!nodes.length) return null;
  const i = selected ? nodes.findIndex((n) => n.id === selected) : -1;
  if (i >= 0) return nodes[forward ? i + 1 : i - 1]?.id ?? null;
  const order: string[] = [];
  const walk = (ns: TreeNode[]) => ns.forEach((n) => { order.push(n.id); walk(n.children); });
  walk(tree);
  const at = selected ? order.indexOf(selected) : -1;
  const pos = (n: TreeNode) => order.indexOf(n.id);
  const target = forward ? nodes.find((n) => pos(n) > at) : [...nodes].reverse().find((n) => pos(n) < (at < 0 ? order.length : at));
  return target?.id ?? null;
}

/** Write's banner: how many of a folder's documents the filter lets through (null when it shows them all). */
export function filteredCounts(fullTree: TreeNode[], result: FilterResult, selected: string | null): { shown: number; total: number } | null {
  const node = selected ? pathOf(fullTree, selected)?.at(-1) : null;
  const roots = node ? node.children : fullTree;
  let shown = 0, total = 0;
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.fragmentId && !n.isFolder) {
        total++;
        if (result.matches.has(n.id)) shown++;
      }
      walk(n.children);
    }
  };
  walk(roots);
  return total && shown < total ? { shown, total } : null;
}

// MARK: Moving while filtered

const siblingsIn = (tree: TreeNode[], parent: string | null): TreeNode[] | null =>
  parent === null ? tree : (pathOf(tree, parent)?.at(-1)?.children ?? null);

/** `unfilteredIndex`: where a placement among the visible rows goes among all the siblings — just after the visible row
 *  above it (or before the first visible one), so hidden documents keep their places. */
export function unfilteredPlacement(full: TreeNode[], visible: TreeNode[], moving: readonly string[], p: Placement): Placement {
  if (!('inside' in p) || p.index === undefined) return p;
  const index = p.index;
  const all = siblingsIn(full, p.inside);
  const shown = siblingsIn(visible, p.inside);
  if (!all || !shown) return p;
  const staying = all.filter((n) => !moving.includes(n.id));
  const shownStaying = shown.filter((n) => !moving.includes(n.id));
  if (index <= 0) {
    const first = shownStaying[0] ? staying.findIndex((n) => n.id === shownStaying[0].id) : -1;
    return { inside: p.inside, index: Math.max(0, first) };
  }
  if (index - 1 >= shownStaying.length) return { inside: p.inside, index: staying.length };
  const above = staying.findIndex((n) => n.id === shownStaying[index - 1].id);
  return { inside: p.inside, index: above < 0 ? staying.length : above + 1 };
}

// MARK: Words for the bar

/** "5 of 29 scenes · 11,240 words". */
/** What the contents call their documents (the Apple apps' `DocumentNoun`), so a journal never says "scenes": a book's or
 *  script's manuscript holds scenes, a journal's posts, and every other arrangement (notes, research, matter) documents. */
export type DocumentNoun = 'scene' | 'post' | 'document';

export function documentNoun(type: ProjectType | null | undefined, manuscriptSide: boolean): DocumentNoun {
  return !manuscriptSide ? 'document' : type === 'journal' ? 'post' : 'scene';
}

/** The noun for an arrangement the contents show: the manuscript's side is the manuscript and its alternates. */
export function arrangementNoun(type: ProjectType | null | undefined, role: string): DocumentNoun {
  return documentNoun(type, role === 'manuscript' || role === 'alternate');
}

/** "12 scenes", "1 post". */
export function nounCount(noun: DocumentNoun, n: number): string {
  return `${formatNumber(n)} ${noun}${n === 1 ? '' : 's'}`;
}

export function countLine(result: FilterResult, noun: DocumentNoun): string {
  return `${formatNumber(result.documents)} of ${formatNumber(result.total)} ${noun}s · ${formatNumber(result.words)} ${result.words === 1 ? 'word' : 'words'}`;
}

const KIND_NAMES: Record<string, string> = {
  document: 'Documents', folder: 'Folders', prose: 'Scenes', screenplay: 'Scenes', note: 'Notes', research: 'Research', 'front-matter': 'Book matter',
};

/** A chip's words: the facet, then its values ("Status: Draft or Revised", "Hyde as POV"). `title` names a node. */
export function facetLabel(rule: SavedSearchRule, project: OpenedProject, title: (nodeId: string) => string | null): string {
  const fields = project.record.fields;
  const entity = (id: string) => project.record.entities.find((e) => e.id === id)?.name ?? 'Someone';
  const or = rule.all ? ' and ' : ' or ';
  const value = ruleValue(rule) ?? '';
  switch (rule.kind) {
    case 'field': {
      const field = fields.find((f) => f.id === rule.field);
      const name = field?.name ?? 'Field';
      if (rule.op === 'contains') return `${name} contains “${value}”`;
      if (rule.values.length === 0 && rule.op !== 'empty') return `${name}: choose…`;
      if (rule.op === 'empty' || (rule.values.length === 1 && rule.values[0] === '')) return rule.negate ? `${name}: any` : `${name}: none`;
      const values = rule.values.map((v) => {
        if (v === '') return 'none';
        const option = field?.options.find((o) => o.id === v);
        if (option) return option.name;
        if (field?.type === 'entity-ref') return entity(v);
        if (field?.type === 'boolean') return ['true', 'yes'].includes(v.toLowerCase()) ? 'yes' : 'no';
        return v;
      }).join(or);
      return rule.negate ? `${name} isn’t ${values}` : `${name}: ${values}`;
    }
    case 'entity': {
      if (!rule.values.length) return rule.level === 'pov' ? 'POV: choose…' : 'Who: choose…';
      const names = rule.values.map(entity).join(or);
      const level = rule.level === 'pov' ? ' as POV' : rule.level === 'on-stage' ? ' on stage' : '';
      return rule.negate ? `Not ${names}${level}` : names + level;
    }
    case 'text': return rule.negate ? `Text without “${value}”` : `Text: “${value}”`;
    case 'title': return rule.negate ? `Title without “${value}”` : `Title: “${value}”`;
    case 'synopsis':
      if (rule.op === 'empty') return rule.negate ? 'Has a synopsis' : 'No synopsis';
      return `Synopsis: “${value}”`;
    case 'notes':
      if (rule.op === 'empty') return rule.negate ? 'Has notes' : 'No notes';
      return `Notes: “${value}”`;
    case 'modified-within': {
      const days = rule.days ?? 30;
      return rule.negate ? `Not changed in ${days} days` : `Changed in the last ${days === 1 ? 'day' : `${days} days`}`;
    }
    case 'included-in-compile': return rule.negate ? 'Not in the compile' : 'In the compile';
    case 'words': {
      const n = Math.trunc(rule.number ?? 0);
      switch (rule.op ?? 'lt') {
        case 'gt': return `Over ${formatNumber(n)} words`;
        case 'between': return `${formatNumber(n)}–${formatNumber(Math.trunc(rule.upper ?? rule.number ?? 0))} words`;
        default: return n === 50 ? 'Unwritten' : `Under ${formatNumber(n)} words`;
      }
    }
    case 'target': return value === 'reached' ? 'Target reached' : 'Under target';
    case 'locked': return rule.negate ? 'Unlocked' : 'Locked';
    case 'kind': {
      if (!rule.values.length) return 'Kind: choose…';
      const names = [...new Set(rule.values.map((v) => KIND_NAMES[v] ?? 'Book matter'))].join(', ');
      return (rule.negate ? 'Not ' : '') + names;
    }
    case 'within': {
      const titles = rule.values.map((id) => title(id) ?? 'a folder').join(or);
      return rule.negate ? `Outside ${titles}` : `In ${titles}`;
    }
    case 'comments': {
      // "Fact-check comments", "No uncategorised comments", "Comments from agents" (as the Apple apps say it).
      const name = readCommentCategories(project.record).find((c) => c.id === rule.category)?.name ?? 'Category';
      let words = rule.category === undefined ? (rule.negate ? 'No comments' : 'Comments')
        : rule.category === 'none' ? (rule.negate ? 'No uncategorised comments' : 'Uncategorised comments')
        : rule.negate ? `No ${name} comments` : `${name} comments`;
      if (rule.author !== undefined) words += rule.author === 'agent' ? ' from agents' : ' from people';
      return words;
    }
    case 'suggestions': return rule.negate ? 'No open suggestions' : 'Open suggestions';
    case 'unsupported': return 'Made in a newer version';
  }
}

// MARK: Suggestions

/** A choice offered as words are typed, or by a row's Filter By. */
export interface FilterSuggestion {
  /** What kind of thing it is ("Character", "Status"); empty for a quick filter. */
  facet: string;
  value: string;
  rules: SavedSearchRule[];
}

const ENTITY_FACETS = { character: 'Character', place: 'Place', object: 'Object', motif: 'Motif' } as const;

/** Facets whose values start with the typed words: characters, places and things, list options, tags. */
export function filterSuggestions(project: OpenedProject, tags: readonly { tag: string }[], typed: string): FilterSuggestion[] {
  const query = new WordQuery(typed);
  if (query.isEmpty) return [];
  const fits = (name: string) => query.matches(name);
  const found: FilterSuggestion[] = [];
  for (const e of project.record.entities) {
    if (fits(e.name) || e.aliases.some(fits)) found.push({ facet: ENTITY_FACETS[e.kind], value: e.name, rules: [newRule({ kind: 'entity', value: e.id })] });
  }
  for (const field of project.fields()) {
    if (field.type !== 'enum') continue;
    for (const option of field.options) {
      if (fits(option.name)) found.push({ facet: field.name, value: option.name, rules: [newRule({ kind: 'field', field: field.id, value: option.id })] });
    }
  }
  const tagsField = project.fields().find((f) => f.id === StandardField.tags);
  if (tagsField) {
    for (const t of tags) if (fits(t.tag)) found.push({ facet: tagsField.name, value: t.tag, rules: [newRule({ kind: 'field', field: StandardField.tags, value: t.tag })] });
  }
  return found.slice(0, 8);
}

/** Ready-made filters: the questions a revision pass asks most. */
export function quickFilters(hasBeatSheet: boolean): FilterSuggestion[] {
  const documents = () => newRule({ kind: 'kind', value: 'document' });
  const quick: FilterSuggestion[] = [
    { facet: '', value: 'Unwritten', rules: [newRule({ kind: 'words', op: 'lt', number: 50 }), documents()] },
    { facet: '', value: 'Needs a synopsis', rules: [newRule({ kind: 'synopsis', op: 'empty' }), documents()] },
    { facet: '', value: 'Has open comments', rules: [newRule({ kind: 'comments' })] },
    { facet: '', value: 'Changed this week', rules: [newRule({ kind: 'modified-within', days: 7 })] },
    { facet: '', value: 'Not in the compile', rules: [newRule({ kind: 'included-in-compile', negate: true })] },
  ];
  if (hasBeatSheet) quick.splice(2, 0, { facet: '', value: 'No beat', rules: [newRule({ kind: 'field', field: StandardField.beat, value: '' }), documents()] });
  return quick;
}

/** Filter By ▸ for a row: its POV, status, label, setting, beat, section type and tags, and the folder it's in. */
export function filterChoices(project: OpenedProject, tree: TreeNode[], nodeId: string): FilterSuggestion[] {
  const path = pathOf(tree, nodeId);
  const node = path?.at(-1);
  if (!node) return [];
  const choices: FilterSuggestion[] = [];
  const f = node.fragmentId ? project.fragmentRecord(node.fragmentId) : null;
  if (f) {
    const fields = project.fields();
    for (const id of [StandardField.pov, StandardField.status, StandardField.label, StandardField.setting, StandardField.beat, StandardField.sectionType]) {
      const field = fields.find((x) => x.id === id);
      const v = f.metadata[id];
      if (!field || v?.type !== 'text' || !v.value) continue;
      const shown = project.displayValue(id, v);
      if (!shown) continue;
      const rule = id === StandardField.pov ? newRule({ kind: 'entity', value: v.value, level: 'pov' }) : newRule({ kind: 'field', field: id, value: v.value });
      choices.push({ facet: field.name, value: shown, rules: [rule] });
    }
    const tags = f.metadata[StandardField.tags];
    if (tags?.type === 'text') {
      for (const tag of tags.value.split(',').map((t) => t.trim()).filter(Boolean)) {
        choices.push({ facet: 'Tag', value: tag, rules: [newRule({ kind: 'field', field: StandardField.tags, value: tag })] });
      }
    }
  }
  const parent = path && path.length > 1 ? path[path.length - 2] : null;
  if (parent) choices.push({ facet: 'In', value: parent.title || 'Untitled', rules: [newRule({ kind: 'within', value: parent.id })] });
  return choices;
}

/** Whether a node can be focused on: a folder, or a document with documents under it. */
export const isFocusable = (n: TreeNode) => n.isFolder || n.children.length > 0;

/** The kinds a Kind facet offers. */
export const KIND_CHOICES: { id: string; name: string }[] = [
  { id: 'prose', name: 'Scenes' }, { id: 'note', name: 'Notes' }, { id: 'research', name: 'Research' }, { id: 'front-matter', name: 'Book matter' },
  { id: 'document', name: 'Documents only' }, { id: 'folder', name: 'Folders only' },
];

/** A project's scene kind (a screenplay's are scripts). */
export const sceneKind = (project: Pick<OpenedProject, 'type'>): FragmentKind => (project.type === 'screenplay' ? 'screenplay' : 'prose');

// MARK: The sidebar's chips, card and dialog (`design-docs/Sidebar redesign — design.md`)

/** `FacetStyle`: a facet's icon, ink and name (the dialog's rule rows, a chip's icon). */
export interface FacetStyle {
  icon: IconName;
  ink: Ink;
  name: string;
}

const ENTITY_NAMES: Record<string, string> = { character: 'Character', place: 'Place', object: 'Object', motif: 'Motif' };

/** The design's table: Status flag/green, Label tag/purple, Tag number/pink, presence person/green, POV eye/red, Setting
 *  mappin/green, other lists list/grey, words blue, Changed calendar/yellow, Words and Word target yellow, the compile,
 *  locks, kinds and folders orange, comments and suggestions purple. */
export function facetStyle(rule: SavedSearchRule, project: Pick<OpenedProject, 'record'>): FacetStyle {
  switch (rule.kind) {
    case 'field': {
      const field = project.record.fields.find((f) => f.id === rule.field);
      const name = field?.name ?? 'Field';
      if (rule.field === StandardField.status) return { icon: 'flag', ink: 'green', name };
      if (rule.field === StandardField.label) return { icon: 'tag', ink: 'purple', name };
      if (field?.type === 'tags') return { icon: 'number', ink: 'pink', name: rule.field === StandardField.tags ? 'Tag' : name };
      if (rule.field === StandardField.pov) return { icon: 'eye', ink: 'red', name };
      if (rule.field === StandardField.setting) return { icon: 'mappin', ink: 'green', name };
      return { icon: 'list', ink: 'grey', name };
    }
    case 'entity': {
      if (rule.level === 'pov') return { icon: 'eye', ink: 'red', name: 'Point of View' };
      const kinds = new Set(rule.values.map((v) => project.record.entities.find((e) => e.id === v)?.kind).filter((k) => k !== undefined));
      const name = kinds.size === 1 ? ENTITY_NAMES[[...kinds][0]!] : 'Character, Place or Object';
      return { icon: 'person', ink: 'green', name };
    }
    case 'text': return { icon: 'text', ink: 'blue', name: 'Text' };
    case 'title': return { icon: 'heading', ink: 'blue', name: 'Title' };
    case 'synopsis': return { icon: 'text-align', ink: 'blue', name: 'Synopsis' };
    case 'notes': return { icon: 'note', ink: 'blue', name: 'Notes' };
    case 'modified-within': return { icon: 'calendar', ink: 'yellow', name: 'Date Modified' };
    case 'words': return { icon: 'number', ink: 'yellow', name: 'Words' };
    case 'target': return { icon: 'target', ink: 'yellow', name: 'Word Target' };
    case 'included-in-compile': return { icon: 'checkmark', ink: 'orange', name: 'Include in Compile' };
    case 'locked': return { icon: 'lock', ink: 'orange', name: 'Locked' };
    case 'kind': return { icon: 'doc', ink: 'orange', name: 'Kind' };
    case 'within': return { icon: 'folder', ink: 'orange', name: 'Within' };
    case 'comments': return { icon: 'bubble', ink: 'purple', name: 'Comments' };
    case 'suggestions': return { icon: 'bubble', ink: 'purple', name: 'Suggestions' };
    case 'unsupported': return { icon: 'list', ink: 'grey', name: 'Newer rule' };
  }
}

/** `facetParts`: a chip's words in two halves — a faint lead (the facet's name, "Label is not", "On stage") and the value
 *  ("Draft or Revised", “fog”) — and the dot of the option's (or person's) colour when there's one value with one. */
export interface FacetParts {
  lead: string;
  value: string;
  dot: string | null;
}

export function facetParts(rule: SavedSearchRule, project: OpenedProject, title: (nodeId: string) => string | null): FacetParts {
  const whole = (): FacetParts => ({ lead: '', value: facetLabel(rule, project, title), dot: null });
  const quoted = (v: string | undefined) => `“${v ?? ''}”`;
  const or = rule.all ? ' and ' : ' or ';
  const entity = (id: string) => project.record.entities.find((e) => e.id === id);
  switch (rule.kind) {
    case 'field': {
      const field = project.record.fields.find((f) => f.id === rule.field);
      const name = field?.name ?? 'Field';
      if (rule.op === 'contains') return { lead: rule.negate ? `${name} without` : name, value: quoted(ruleValue(rule)), dot: null };
      const lead = rule.negate ? `${name} is not` : name;
      if (rule.values.length === 0 && rule.op !== 'empty') return { lead: name, value: 'choose…', dot: null };
      if (rule.op === 'empty') return { lead, value: 'None', dot: null };
      const colour = (v: string) => cssColor(field?.options.find((o) => o.id === v)?.color ?? (field?.type === 'entity-ref' ? entity(v)?.color : undefined));
      const value = rule.values.map((v) => {
        if (v === '') return 'None';
        const option = field?.options.find((o) => o.id === v);
        if (option) return option.name;
        if (field?.type === 'entity-ref') return entity(v)?.name ?? 'Someone';
        if (field?.type === 'boolean') return ['true', 'yes'].includes(v.toLowerCase()) ? 'Yes' : 'No';
        return v;
      }).join(or);
      return { lead, value, dot: rule.values.length === 1 && rule.values[0] !== '' ? colour(rule.values[0]) : null };
    }
    case 'entity': {
      const lead = rule.level === 'pov' ? (rule.negate ? 'Not POV' : 'POV') : rule.level === 'on-stage' ? (rule.negate ? 'Not on stage' : 'On stage')
        : rule.negate ? 'Doesn’t appear' : 'Appears';
      if (!rule.values.length) return { lead, value: 'choose…', dot: null };
      const value = rule.values.map((v) => entity(v)?.name ?? 'Someone').join(or);
      return { lead, value, dot: rule.values.length === 1 ? cssColor(entity(rule.values[0])?.color) : null };
    }
    case 'text': case 'title': case 'synopsis': case 'notes': {
      if (rule.op === 'empty') return whole();
      const name = rule.kind === 'text' ? 'Text' : rule.kind === 'title' ? 'Title' : rule.kind === 'synopsis' ? 'Synopsis' : 'Notes';
      return { lead: rule.negate ? `${name} without` : name, value: quoted(ruleValue(rule)), dot: null };
    }
    default:
      return whole();
  }
}

/** `SavedSearch.Scope` in the dialog's words. */
export const SCOPE_NAMES: Record<SavedSearch['scope'], string> = { manuscript: 'Manuscript', research: 'Research', 'all-text': 'Everything', unsupported: 'Elsewhere' };

/** The card's caption ("Matches any · Manuscript"): all or any only with two or more rules, the scope only for a
 *  collection. Empty when neither says anything. */
export function filterCaption(rules: number, matchAll: boolean, saved: Pick<SavedSearch, 'scope'> | null): string {
  const parts: string[] = [];
  if (rules >= 2) parts.push(matchAll ? 'Matches all' : 'Matches any');
  if (saved) parts.push(SCOPE_NAMES[saved.scope]);
  return parts.join(' · ');
}

/** The scope a filter saved from this arrangement looks in. */
export const scopeFor = (role: string): SavedSearch['scope'] => (role === 'manuscript' || role === 'alternate' ? 'manuscript' : role === 'reference' ? 'research' : 'all-text');

/** A folder's figures: its documents (not folders) under it and how many of them the filter shows ("3 of 5") — every
 *  node with a fragment that isn't a folder, as `FilterResult` counts documents — and the words under it with its own
 *  (what the row shows when nothing's filtered), by `words` of a fragment id. Folders only (a node with children, or a
 *  folder). */
export interface FolderCount { shown: number; total: number; words: number }

export function folderCounts(tree: TreeNode[], view: Pick<FilterView, 'result' | 'kept'> | null, words: (fragmentId: string) => number = () => 0): Map<string, FolderCount> {
  const out = new Map<string, FolderCount>();
  const walk = (n: TreeNode): FolderCount => {
    const sum: FolderCount = { shown: 0, total: 0, words: n.fragmentId ? words(n.fragmentId) : 0 };
    for (const c of n.children) {
      if (c.fragmentId && !c.isFolder) {
        sum.total++;
        if (!view || view.result.matches.has(c.id) || view.kept === c.id) sum.shown++;
      }
      const below = walk(c);
      sum.shown += below.shown;
      sum.total += below.total;
      sum.words += below.words;
    }
    if (n.isFolder || n.children.length) out.set(n.id, sum);
    return sum;
  };
  tree.forEach(walk);
  return out;
}

/** A folder row's figure: "3 of 5" while filtering, else the words under it (none when there are none). */
export function folderDetail(count: FolderCount, filtering: boolean): string | null {
  if (filtering) return `${formatNumber(count.shown)} of ${formatNumber(count.total)}`;
  return count.words > 0 ? formatNumber(count.words) : null;
}

/** "12 scenes hidden" / "12 scenes shown faded" (the contents' foot): the documents the filter leaves out. */
export function leftOutLine(view: Pick<FilterView, 'result'>, noun: DocumentNoun, dims: boolean): string | null {
  const n = view.result.total - view.result.documents;
  if (n <= 0) return null;
  return `${nounCount(noun, n)} ${dims ? 'shown faded' : 'hidden'}`;
}

/** The dialog's condition button: its words, and the rule turned round (is / is not, includes / excludes, contains /
 *  doesn't contain, within / not within; Words steps under → over → between). Null: nothing to turn. */
export function ruleCondition(rule: SavedSearchRule, fields: readonly { id: string; type: string }[]): { label: string; next: SavedSearchRule } | null {
  const flip = (yes: string, no: string) => ({ label: rule.negate ? no : yes, next: { ...rule, negate: !rule.negate } });
  switch (rule.kind) {
    case 'field': {
      if (rule.op === 'contains') return flip('contains', 'doesn’t contain');
      if (fields.find((f) => f.id === rule.field)?.type === 'tags') return flip('includes', 'excludes');
      return flip('is', 'is not');
    }
    case 'entity': return flip('includes', 'excludes');
    case 'text': case 'title': case 'synopsis': case 'notes':
      return rule.op === 'empty' ? flip('is empty', 'isn’t empty') : flip('contains', 'doesn’t contain');
    case 'modified-within': return flip('within', 'not within');
    case 'comments': case 'suggestions': return flip('has', 'hasn’t');
    case 'words': {
      const order = ['lt', 'gt', 'between'] as const;
      const op = rule.op === 'gt' || rule.op === 'between' ? rule.op : 'lt';
      const next = order[(order.indexOf(op) + 1) % order.length];
      return { label: op === 'gt' ? 'over' : op === 'between' ? 'between' : 'under', next: { ...rule, op: next, ...(next === 'between' && rule.upper === undefined ? { upper: (rule.number ?? 0) + 1000 } : {}) } };
    }
    case 'included-in-compile': case 'locked': case 'kind': case 'target': case 'within':
      return flip('is', 'is not');
    case 'unsupported': return null;
  }
}

/** One of Add Rule's choices: what it's called, a line on what it tests, and the rule it adds. */
export interface RuleChoice {
  id: string;
  name: string;
  description: string;
  make: () => SavedSearchRule;
}

/** Add Rule's panel, in groups — Properties, Content, Metadata, Your fields — each only when the project has it. */
export function ruleChoices(project: OpenedProject, hasTags: boolean): { title: string; items: RuleChoice[] }[] {
  const fields = project.fields();
  const has = (id: string) => fields.some((f) => f.id === id && (f.type !== 'enum' || f.options.length > 0));
  const entities = project.record.entities;
  const field = (id: string, name: string, description: string): RuleChoice[] =>
    has(id) ? [{ id, name, description, make: () => newRule({ kind: 'field', field: id }) }] : [];
  const properties: RuleChoice[] = [
    ...field(StandardField.status, 'Status', 'Where it is in your workflow'),
    ...field(StandardField.label, 'Label', 'Its coloured label'),
    ...(hasTags && fields.some((f) => f.id === StandardField.tags) ? [{ id: 'tags', name: 'Tag', description: 'A word or phrase on the document', make: () => newRule({ kind: 'field', field: StandardField.tags }) }] : []),
    ...(entities.length ? [{ id: 'entity', name: 'Character, Place or Object', description: 'Who or what appears', make: () => newRule({ kind: 'entity' }) }] : []),
    ...(entities.some((e) => e.kind === 'character') ? [{ id: 'pov', name: 'Point of View', description: 'Whose eyes it’s seen through', make: () => newRule({ kind: 'entity', level: 'pov' }) }] : []),
    ...(entities.some((e) => e.kind === 'place') ? field(StandardField.setting, fields.find((f) => f.id === StandardField.setting)?.name ?? 'Setting', 'Where it happens') : []),
    ...field(StandardField.sectionType, 'Section Type', 'Part, chapter or scene'),
    ...(beatSheetOf(project.record.settings) ? field(StandardField.beat, 'Beat', 'Its beat on the beat sheet') : []),
    { id: 'kind', name: 'Kind', description: 'Scenes, notes, research, folders…', make: () => newRule({ kind: 'kind' }) },
  ];
  const content: RuleChoice[] = [
    { id: 'text', name: 'Text', description: 'Words in the text', make: () => newRule({ kind: 'text' }) },
    { id: 'title', name: 'Title', description: 'Words in the title', make: () => newRule({ kind: 'title' }) },
    { id: 'synopsis', name: 'Synopsis', description: 'Words in the synopsis', make: () => newRule({ kind: 'synopsis' }) },
    { id: 'notes', name: 'Notes', description: 'Words in the notes', make: () => newRule({ kind: 'notes' }) },
  ];
  const metadata: RuleChoice[] = [
    { id: 'modified-within', name: 'Date Modified', description: 'When it was last changed', make: () => newRule({ kind: 'modified-within', days: 7 }) },
    { id: 'included-in-compile', name: 'Include in Compile', description: 'Part of the book you publish', make: () => newRule({ kind: 'included-in-compile' }) },
    { id: 'words', name: 'Words', description: 'How long it is', make: () => newRule({ kind: 'words', op: 'lt', number: 1000 }) },
    { id: 'target', name: 'Word Target', description: 'Short of its target, or past it', make: () => newRule({ kind: 'target', value: 'under' }) },
    { id: 'locked', name: 'Locked', description: 'Locked against change', make: () => newRule({ kind: 'locked' }) },
    { id: 'comments', name: 'Comments', description: 'Open comments on it', make: () => newRule({ kind: 'comments' }) },
    { id: 'suggestions', name: 'Suggestions', description: 'Open suggestions on it', make: () => newRule({ kind: 'suggestions' }) },
  ];
  const own = fields.filter((f) => !STANDARD_FIELDS.has(f.id) && ['enum', 'boolean', 'text', 'tags'].includes(f.type) && (f.type !== 'enum' || f.options.length));
  const yours: RuleChoice[] = own.map((f) => ({
    id: `field-${f.id}`,
    name: f.name,
    description: f.type === 'enum' ? 'One of its choices' : f.type === 'boolean' ? 'Yes or no' : f.type === 'tags' ? 'Tags' : 'Words in it',
    make: () => newRule(f.type === 'boolean' ? { kind: 'field', field: f.id, value: 'true' } : f.type === 'text' ? { kind: 'field', field: f.id, op: 'contains' } : { kind: 'field', field: f.id }),
  }));
  return [
    { title: 'Properties', items: properties },
    { title: 'Content', items: content },
    { title: 'Details', items: metadata },
    ...(yours.length ? [{ title: 'Your fields', items: yours }] : []),
  ];
}

const STANDARD_FIELDS = new Set(['status', 'label', 'pov', 'word-target', 'section-type', 'beat', 'post-number', 'publish-date', 'tags', 'story-time', 'story-duration', 'setting']);
